import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Badge } from './Badge';
import { formatDate } from '../utils/format';
import { enumLabel } from '../utils/i18nLabels';

/**
 * Ficha premium demo/reutilizable — combina datos del directorio con microcopy ejecutivo.
 * @param {{ employee: object, managerName?: string | null, onClose: () => void }} props
 */
export function EmployeeProfilePremium({ employee, managerName, onClose }) {
  const { t } = useApp();

  const fullName = `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || '—';

  const tenureLabel = useMemo(() => {
    if (!employee.start_date) return '—';
    const s = new Date(employee.start_date);
    if (Number.isNaN(s.getTime())) return formatDate(employee.start_date);
    const months = Math.max(0, Math.floor((Date.now() - s.getTime()) / (86400000 * 30.44)));
    return months < 1 ? t('employees.tenureUnderOne') : `${months} ${t('employees.tenureMonthShort')}`;
  }, [employee.start_date, t]);

  const timeline = useMemo(
    () => [
      { k: 'employees.timeline1', date: employee.start_date },
      { k: 'employees.timeline2', date: null },
      { k: 'employees.timeline3', date: null },
      { k: 'employees.timeline4', date: null },
    ],
    [employee.start_date]
  );

  return (
    <section className="emp-profile-premium panel panel--elevated" aria-labelledby="emp-profile-premium-title">
      <div className="emp-profile-premium__head">
        <div>
          <h2 id="emp-profile-premium-title" className="emp-profile-premium__title">
            {t('employees.empProfileTitle')}
          </h2>
          <p className="panel__muted text-sm">{t('employees.empProfileLead')}</p>
        </div>
        <div className="emp-profile-premium__head-actions">
          <Badge variant={employee.status === 'active' ? 'approved' : 'pending'}>{enumLabel(t, 'employeeStatus', employee.status)}</Badge>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
            {t('employees.profilePremiumClose')}
          </button>
        </div>
      </div>

      <p className="panel__muted text-sm emp-profile-premium__demo">{t('employees.empProfileDemoNote')}</p>

      <div className="emp-profile-premium__hero">
        <div className="emp-profile-premium__avatar" aria-hidden>
          {(employee.first_name?.[0] || '?').toUpperCase()}
          {(employee.last_name?.[0] || '').toUpperCase()}
        </div>
        <div>
          <h3 className="emp-profile-premium__name">{fullName}</h3>
          <p className="panel__muted">{employee.email || '—'}</p>
          <div className="emp-profile-premium__chips">
            <span className="dash-pill dash-pill--muted">{employee.area || '—'}</span>
            <span className="dash-pill dash-pill--ok">{employee.position || t('employees.empProfileRole')}</span>
          </div>
        </div>
      </div>

      <div className="emp-profile-premium__grid">
        <div className="emp-profile-premium__card">
          <h4 className="emp-profile-premium__card-title">{t('employees.empProfilePersonal')}</h4>
          <dl className="emp-profile-premium__dl">
            <div>
              <dt>{t('employees.fieldPhone')}</dt>
              <dd>{employee.phone || '—'}</dd>
            </div>
            <div>
              <dt>{t('employees.fieldEmail')}</dt>
              <dd>{employee.email || '—'}</dd>
            </div>
          </dl>
        </div>
        <div className="emp-profile-premium__card">
          <h4 className="emp-profile-premium__card-title">{t('employees.empProfileWork')}</h4>
          <dl className="emp-profile-premium__dl">
            <div>
              <dt>{t('employees.empProfileStatus')}</dt>
              <dd>{enumLabel(t, 'employeeStatus', employee.status)}</dd>
            </div>
            <div>
              <dt>{t('employees.empProfileArea')}</dt>
              <dd>{employee.area || '—'}</dd>
            </div>
            <div>
              <dt>{t('employees.fieldDepartment')}</dt>
              <dd>{employee.department || '—'}</dd>
            </div>
            <div>
              <dt>{t('employees.empProfileSeniority')}</dt>
              <dd>{t('employees.empProfileSeniorityDemo')}</dd>
            </div>
            <div>
              <dt>{t('employees.empProfileStart')}</dt>
              <dd>{employee.start_date ? formatDate(employee.start_date) : '—'}</dd>
            </div>
            <div>
              <dt>{t('employees.empProfileTenure')}</dt>
              <dd>{tenureLabel}</dd>
            </div>
            <div>
              <dt>{t('employees.empProfileManager')}</dt>
              <dd>{managerName || '—'}</dd>
            </div>
          </dl>
        </div>
      </div>

      <div className="emp-profile-premium__links">
        <Link to={`/records?employee_id=${employee.id}`} className="btn btn--secondary btn--sm">
          {t('employees.empProfileDocs')} →
        </Link>
        <Link to="/leaves" className="btn btn--secondary btn--sm">
          {t('employees.empProfileLeaves')} →
        </Link>
        <Link to="/performance" className="btn btn--secondary btn--sm">
          {t('employees.empProfileEval')} →
        </Link>
        <Link to="/training" className="btn btn--secondary btn--sm">
          {t('employees.empProfileTrain')} →
        </Link>
        <Link to="/communications" className="btn btn--secondary btn--sm">
          {t('employees.empProfileComms')} →
        </Link>
        <Link to="/expense-reports" className="btn btn--secondary btn--sm">
          {t('employees.empProfileExpenses')} →
        </Link>
      </div>
      <ul className="panel__muted text-sm emp-profile-premium__hints">
        <li>{t('employees.empProfileDocsHint')}</li>
        <li>{t('employees.empProfileLeavesHint')}</li>
        <li>{t('employees.empProfileEvalHint')}</li>
      </ul>

      <div className="emp-profile-premium__card emp-profile-premium__card--wide">
        <h4 className="emp-profile-premium__card-title">{t('employees.empProfileTimeline')}</h4>
        <ol className="emp-profile-premium__timeline">
          {timeline.map((row, i) => (
            <li key={i} className="emp-profile-premium__timeline-row">
              <span className="emp-profile-premium__timeline-dot" aria-hidden />
              <div>
                <span className="panel__muted text-sm">{row.date ? formatDate(row.date) : '—'}</span>
                <p className="emp-profile-premium__timeline-txt">{t(row.k)}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      <div className="emp-profile-premium__card emp-profile-premium__card--accent">
        <h4 className="emp-profile-premium__card-title">{t('employees.empProfileNext')}</h4>
        <ol className="emp-profile-premium__next-list">
          <li>{t('employees.empProfileNext1')}</li>
          <li>{t('employees.empProfileNext2')}</li>
          <li>{t('employees.empProfileNext3')}</li>
        </ol>
      </div>
    </section>
  );
}
